import type { Image } from '../../types';

export type ImageSourceOptions = {
    baseCdnUrl: string;
    defaultResolution: number;
    imageSizes: ImageSizes;
};

export type ImageSizes = [number, string][];

type ImageSource = {
    src?: string;
    srcSet?: string;
};

export function getImageSource(
    { url, path, width }: Partial<Image>,
    options: ImageSourceOptions,
): ImageSource {
    const { baseCdnUrl, defaultResolution, imageSizes } = options;

    const imageUrl = path ? `${baseCdnUrl}/default/${path}` : url;
    const isStaticImage = !imageUrl?.includes('/dynamic/default/');

    const imageSource: ImageSource = {};

    if (imageUrl) {
        imageSource.src = isStaticImage
            ? imageUrl
            : getDefaultImageUrl(imageUrl, defaultResolution, imageSizes);
    }
    if (imageUrl && width && !isStaticImage) {
        imageSource.srcSet = getImageSourceSet(imageUrl, width, imageSizes);
    }

    return imageSource;
}

function getDefaultImageUrl(
    url: string,
    defaultResolution: number,
    imageSizes: ImageSizes,
): string {
    const validSize = imageSizes.find(([width]) => width === defaultResolution);
    const defaultSize = (validSize && validSize[1]) ?? `${defaultResolution}w`;

    return formatUrl(url, defaultSize);
}

function getImageSourceSet(
    url: string,
    width: number,
    imageSizes: ImageSizes,
): string {
    const validSizes = getValidSizes(width, imageSizes);
    const srcSetUrls = validSizes.map(
        ([width, size]) => `${formatUrl(url, size)} ${width}w`,
    );
    return srcSetUrls.join(', ');
}

function getValidSizes(imageWidth: number, imageSizes: ImageSizes): ImageSizes {
    const validSizes = imageSizes.filter(([width]) => width <= imageWidth);

    if (validSizes.length < 1) {
        return validSizes;
    }

    const largestSelectedSize = validSizes[validSizes.length - 1];
    const nextAvailableSize = imageSizes.find(
        ([availableWidth]) => availableWidth > largestSelectedSize[0],
    );
    const isMissingActualImageSize =
        largestSelectedSize &&
        nextAvailableSize &&
        largestSelectedSize[0] < imageWidth &&
        largestSelectedSize[0] < nextAvailableSize[0];
    if (isMissingActualImageSize) {
        // If the width of the image is not in the validSizes map add it as the last value. Without this, in
        // the case of `maxWidth` being 900, and `validSizes.keys()` being [640, 960], the generated value would
        // be "/dynamic/640w.jpg 640w". This makes sure "/dynamic/960w.jpg 900w, /dynamic/640w.jpg 640w" is generated,
        // since "/dynamic/960w.jpg" is known to be 900 px wide.

        validSizes.push([imageWidth, nextAvailableSize[1]]);
    }

    return validSizes;
}

function formatUrl(url: string, size: string): string {
    return url.replace('/dynamic/default/', `/dynamic/${size}/`);
}
